import * as React from 'react';
import { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import { createFormWidget } from '../helpers/PageRenderHelpers';
import { CreateFormViewModel } from '../helpers/create-form-viewmodel';
import { IFormDefinition } from '../../../interfaces/FormDefinition';

export interface IPopupProps {
  /**
   * Indicates the initial open/close state.
   */
  opened: boolean;
  /**
   * Indicates the size of the modal.
   */
  size?: 'sm' | 'lg' | 'xl';
  /**
   * The title of the popup.
   */
  title: string;
  /**
   * Optional (html)content to render inside the popup. Note that the use of children is also supported.
   */
  content?: string;
  /**
   * An optional header image.
   */
  headerImage?: string;

  /**
   * An optional callback to invoke on closing the popup.
   */
  onHide?: () => Promise<any>;

  /**
   * An optional CTA url.
   */
  ctaUrl?: string;
  /**
   * The text to display on the CTA.
   */
  ctaText?: string;

  /**
   * An optional form name to render, using the formDefinition.
   */
  formName?: string;
  /**
   * The form definition to use when rendering a form.
   */
  formDefinition?: IFormDefinition;

  /**
   * Optionally provide children to render content.
   */
  children?: any;
}

/**
 * A component for displaying popups.
 * @param props The component's properties.
 * @returns 
 */
export const Popup = (props: IPopupProps) => {
  const [show, setShow] = useState(!!props.opened);

  useEffect(() => {
    setShow(!!props.opened);
    setWindowState(true, window.location.pathname);

    // Use popstate in order for back button to close modal
    window.addEventListener('popstate', event => {
    if (event.state) {
      hideDialog();
    }
  });
  }, [props.opened]);
  
  const hideDialog = () => {
    setShow(false);
  };

  const setWindowState = (isPopUp, name) => {
    window.history.pushState(isPopUp, name);
  };

  const onHide = (refreshPage? : boolean) => {
    
    setShow(false);
    setWindowState(false, window.location.pathname);
    const hideCallback = props.onHide ? props.onHide() : Promise.resolve();

    return hideCallback.then(() => {
      if (!!refreshPage) {
        location.reload();
      }
    });
  };

  const onHideClickEvent = (event) => {
    event.preventDefault();
    onHide();
    return false;
  };

  let headerImage = props.headerImage;
  if (headerImage?.startsWith('~')) {
    headerImage = headerImage.slice(1);      
  }

  let ctaUrl = props.ctaUrl;
  let ctaText = props.ctaText;
  let isExternalLink = false;
  let targetLink = '';
  if (ctaUrl && ctaText) {
    if (ctaUrl.startsWith('~')) {
      ctaUrl = ctaUrl.slice(1);
      isExternalLink = true;      
    } else {
      isExternalLink = ctaUrl.startsWith('http');
      targetLink = '_blank';
    }
  }

  const formName = props.formName;
  const formViewModel = formName ? CreateFormViewModel(
      props.formDefinition,
      formName,
      '',
      '',
      {
        fillViaApi: '/internal/formandcontentreact/get/' + formName,
        startForm: 'form',
        formSteps: ['form'],
        formTitle: '',
        postUrl: '/internal/formandcontentreact/set/' + formName,
        standaloneFormInSite: true,
        doFormMultipartPost: true,
        thanksPageUrl: '',
        errorPageUrl: '',
        procesHandlers: 'popup',
        OnSubmitSuccessLinkIncludeUrlGuid: true,
        reactRouteOnSuccess: '',
        reactRouteOnFailure: '-',
        noAutoGenerateSummary: true,
        thanksPageUrlFields: [],
        requiredFieldTip: '',
        overrideBtnFormSubmitClassName: 'btn-secondary2',
        submitButtonIsDisabledIfFormIsNotDirty : true,
        pageSettings: {
          form: {
            title: 'Default form',
            subtitle: ''
          }
        }
      }
  ) : null;

  if (formViewModel) {
    formViewModel.editMode = true;
    formViewModel.IncludeCurrentPageAsBackAndSubmitUrl = false;
  }

  return(
      <Modal  
        className="form__popup form__popup-cta form__popup--modern" 
        centered 
        show={show}
        size={props.size || 'lg'}
        onShow={() => {
          setWindowState(true, window.location.pathname);
        }}
        onHide={() => {     
          onHide();
        }}>
        {headerImage && <div className="form__popup-header-foto"
          style={{
            backgroundImage: `url("${headerImage}")`}}><a href="#" className="form__popup-close icon icon-cross" onClick={onHideClickEvent} aria-label="Close"></a></div>}    
      <Modal.Header className="form__popup-header">
        <Modal.Title>{props.title}</Modal.Title>
        {!headerImage && <a href="#" className="form__popup-close icon icon-cross" onClick={onHideClickEvent} aria-label="Close"></a>}    
      </Modal.Header>

      <Modal.Body className="form__popup-content">    
        <div className="form__popup-content-text">
          {props.content && ReactHtmlParser(props.content)}
          {props.children}
        </div>
          
        {formViewModel && <div className="row">
          <div className="col-12 mijn-fnv__widget-col-wrapper editpage-container__layout-modern">
              {createFormWidget(
                  formViewModel,
                  'widget mijn-fnv__widget',
                  1,
                  'edit',
                  undefined,
                  () => {
                    return onHide(true);
                  }
              )}
          </div>
        </div>}  
      </Modal.Body>

      {!formViewModel && ctaText && ctaUrl ? 
        <Modal.Footer className="form__popup-buttons">
          {isExternalLink ? 
            <a href={ctaUrl} onClick={onHideClickEvent} target={targetLink} className="btn btn-success w-100">{ctaText}</a> : 
            <Link to={ctaUrl} onClick={onHideClickEvent} className="btn btn-success w-100">{ctaText}</Link>
          }
        </Modal.Footer>
        : <div className="mb-4"></div>
      }
    </Modal>);
};